import { InternalPDFHighlight } from "./types";
import { PdfJs } from "@react-pdf-viewer/core";
import { RenderHighlightTargetProps } from "@react-pdf-viewer/highlight";
import { IPdfHighlight } from "~/typings/annotate/annotation";

export function highlightsToInternalHighlights(
  highlights: IPdfHighlight[],
  document: PdfJs.PdfDocument,
): Promise<InternalPDFHighlight[]> {
  return Promise.all(
    highlights
      .map((x) => {
        if (!x.xfdfString) {
          return null;
        }
        let parser = new DOMParser();
        let xmlDoc = parser.parseFromString(x.xfdfString, "text/xml");
        let highlightNode = xmlDoc.firstChild?.firstChild
          ?.firstChild as Element | null;
        if (!highlightNode) {
          return null;
        }

        const rect = highlightNode.getAttribute("coords");
        if (!rect) {
          return null;
        }
        let page = highlightNode.getAttribute("page") as any;
        if (page === null || page === undefined) {
          return;
        }
        page = parseInt(page);
        return document
          .getPage(page + 1)
          .then((p) => {
            const viewPort = p.getViewport({ scale: 1 });
            const height = viewPort.height;
            const width = viewPort.width;
            // rect: [left, bottom, right, top]
            // coords [top left, top right, bottom left, bottom right] all as xy coords
            const split = rect?.split(",");

            const result = split.reduce((prev, next, index) => {
              if (index % 8 == 0) {
                // top left x
                return [...prev, { topLeft: { x: parseInt(next) } }];
              }
              let currentItem = prev[prev.length - 1];
              if (index % 8 == 1) {
                // top left y
                currentItem.topLeft.y = parseInt(next);
              } else if (index % 8 == 2) {
                // top left y
                currentItem.topRight = { x: parseInt(next) };
              } else if (index % 8 == 3) {
                // top left y
                currentItem.topRight.y = parseInt(next);
              } else if (index % 8 == 4) {
                // top left y
                currentItem.bottomLeft = { x: parseInt(next) };
              } else if (index % 8 == 5) {
                // top left y
                currentItem.bottomLeft.y = parseInt(next);
              } else if (index % 8 == 6) {
                // top left y
                currentItem.bottomRight = { x: parseInt(next) };
              } else if (index % 8 == 7) {
                // top left y
                currentItem.bottomRight.y = parseInt(next);
              }
              return prev;
            }, [] as any[]);
            const highlightAreas = result.map((x) => {
              const left = x.topLeft.x;
              const right = x.topRight.x;
              const top = height - x.topLeft.y;
              const bottom = height - x.bottomLeft.y;
              return {
                page: page,
                height: ((bottom - top) / height) * 100,
                left: (left / width) * 100,
                top: (top / height) * 100,
                width: ((right - left) / width) * 100,
              };
            });
            return {
              id: x.id,
              highlightAreas,
              highlight: x,
              color: x.color,
            } as InternalPDFHighlight;
          })
          .catch((err) => {
            return null;
            console.error("Error converting coordinates", err);
          });
      })
      .filter((x) => x) as any[],
  );
}

export function extractXfdfData(
  viewport: PdfJs.ViewPort,
  renderProps: RenderHighlightTargetProps,
) {
  const height = viewport.height;
  const width = viewport.width;
  const xfdfData = renderProps.highlightAreas.reduce(
    (prev, next) => {
      const top = (next.top / 100) * height;
      const left = (next.left / 100) * width;
      const right = (next.width / 100) * width + left;
      const bottom = (next.height / 100) * height + top;
      // Top and bottom needs to be from the bottom left corner.
      const xfdfTop = height - top;
      const xfdfBottom = height - bottom;
      // And now we put them into coords in the correct order:
      // top left x,y, top right xy, bottom left xy, bottom right xy.
      let rect = prev.rect;
      if (!prev.rect.length) {
        rect = [left, xfdfBottom, right, xfdfTop];
      } else {
        rect = [
          Math.min(left, rect[0]),
          Math.min(xfdfBottom, rect[1]),
          Math.max(right, rect[2]),
          Math.max(xfdfTop, rect[3]),
        ];
      }
      return {
        coords: [
          ...prev.coords,
          left,
          xfdfTop,
          right,
          xfdfTop,
          left,
          xfdfBottom,
          right,
          xfdfBottom,
        ],
        rect: rect,
      };
    },
    { coords: [], rect: [] } as { coords: number[]; rect: number[] },
  );
  return xfdfData;
}
